import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  })
}

export function parseTime(timeStr: string): Date {
  const [hours, minutes] = timeStr.split(':').map(Number)
  const date = new Date()
  date.setHours(hours, minutes, 0, 0)
  return date
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency: 'SGD'
  }).format(amount)
}

export function formatDate(date: Date): string {
  return date.toLocaleDateString('en-SG', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  })
}

/**
 * The one zone every Yakson outlet runs in, and the zone every timestamp on
 * screen has to read in. Mirrors `report_service.OUTLET_TIMEZONE`.
 */
export const OUTLET_TIME_ZONE = 'Asia/Singapore'

/**
 * Read a server timestamp as the UTC instant it actually is.
 *
 * Every `createdAt` / `updatedAt` column is a Postgres `timestamp(3)` WITHOUT
 * time zone holding **naive UTC** (see `backend/app/models/_types.py::now_py`),
 * and Pydantic serialises it with no offset — `2026-10-05T03:12:45.123`. Per the
 * ECMAScript date grammar a date-time with no offset is **local** time, so
 * `new Date(iso)` reads that as 03:12 Singapore and the screen prints the UTC
 * clock. Stamping the missing `Z` on is what makes the instant unambiguous —
 * without it no `timeZone` option can help, because the wrong instant is
 * already in hand.
 *
 * A stamp that already carries `Z` or an offset is left alone, so this stays
 * correct the day the backend starts sending tz-aware datetimes.
 */
function parseServerStamp(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const normalized = /(?:Z|z|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`
  const parsed = new Date(normalized)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/** `2026-10-05T03:12:45` → `5 Oct 2026, 11:12` (Singapore). */
export function formatServerDateTime(
  iso: string | null | undefined,
  fallback = '—'
): string {
  const parsed = parseServerStamp(iso)
  if (parsed === null) return fallback
  return parsed.toLocaleString('en-GB', {
    timeZone: OUTLET_TIME_ZONE,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

/** `2026-10-05T19:30:00` → `6 Oct 2026` (Singapore — a late-evening UTC stamp
 *  is already the next day here, which a date-only format must not lose). */
export function formatServerDate(
  iso: string | null | undefined,
  fallback = '—'
): string {
  const parsed = parseServerStamp(iso)
  if (parsed === null) return fallback
  return parsed.toLocaleDateString('en-GB', {
    timeZone: OUTLET_TIME_ZONE,
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** `2026-10-05T19:30:00` → `06/Oct/2026` (Singapore day, dd/Mmm/yyyy). */
export function formatServerDateSlash(
  iso: string | null | undefined,
  fallback = '—'
): string {
  const parsed = parseServerStamp(iso)
  if (parsed === null) return fallback
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: OUTLET_TIME_ZONE,
    day: '2-digit',
    month: 'numeric',
    year: 'numeric'
  }).formatToParts(parsed)
  const pick = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  const monthName = SHORT_MONTHS[Number(pick('month')) - 1] ?? ''
  return `${pick('day')}/${monthName}/${pick('year')}`
}

/**
 * A date of birth as `dd/Mmm/yyyy` — `1992-05-05` → `05/May/1992` (CUS-08-02,
 * the same style as the calendar block, OPS-10). A birthday is a calendar date,
 * not an instant, so it is read straight from its `yyyy-mm-dd` digits and never
 * through `new Date()` (which could shift it a day across time zones).
 */
export function formatDateOfBirth(
  value: string | null | undefined,
  fallback = '-'
): string {
  if (!value) return fallback
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (!match) return fallback
  const [, year, month, day] = match
  const monthName = SHORT_MONTHS[Number(month) - 1]
  if (!monthName) return fallback
  return `${day}/${monthName}/${year}`
}
